// Lógica de la Calculadora Contratos (futuros), sacada de calculadora-contratos.html.
// Módulo puro, sin React: las fórmulas son las del original.

export interface Instrumento {
    id: string;
    name: string;
    pv: number;   // $ por punto
    tick: number; // tamaño del tick en puntos
    micro: boolean;
}

export const INST: Instrumento[] = [
    { id: 'MNQ', name: 'MNQ · Micro Nasdaq-100', pv: 2, tick: 0.25, micro: true },
    { id: 'NQ', name: 'NQ · E-mini Nasdaq-100', pv: 20, tick: 0.25, micro: false },
    { id: 'MES', name: 'MES · Micro S&P 500', pv: 5, tick: 0.25, micro: true },
    { id: 'ES', name: 'ES · E-mini S&P 500', pv: 50, tick: 0.25, micro: false },
    { id: 'MGC', name: 'MGC · Micro Oro', pv: 10, tick: 0.1, micro: true },
    { id: 'GC', name: 'GC · Oro', pv: 100, tick: 0.1, micro: false },
    { id: 'MYM', name: 'MYM · Micro Dow', pv: 0.5, tick: 1, micro: true },
    { id: 'YM', name: 'YM · E-mini Dow', pv: 5, tick: 1, micro: false },
    { id: 'M2K', name: 'M2K · Micro Russell 2000', pv: 5, tick: 0.1, micro: true },
    { id: 'RTY', name: 'RTY · E-mini Russell 2000', pv: 50, tick: 0.1, micro: false },
    { id: 'MCL', name: 'MCL · Micro Petróleo', pv: 100, tick: 0.01, micro: true },
    { id: 'CL', name: 'CL · Petróleo', pv: 1000, tick: 0.01, micro: false },
];

export type Modo = 'risk' | 'qty';
export type Direccion = 'long' | 'short';

// Los precios, el riesgo y los contratos se guardan como texto, tal y como se escriben
export interface ContratosState {
    mode: Modo;
    dir: Direccion;
    inst: string;
    entry: string;
    sl: string;
    tp: string;
    risk: string;
    qty: string;
}

export const KEY = 'calc-contratos-v1';
export const DEF: ContratosState = { mode: 'risk', dir: 'long', inst: 'MNQ', entry: '29440.25', sl: '29400', tp: '29520', risk: '250', qty: '3' };

// Mezcla un estado guardado (localStorage o Firestore) con los valores por defecto
export function mergeState(s: unknown): ContratosState {
    const st = { ...DEF };
    if (s && typeof s === 'object') {
        (Object.keys(DEF) as (keyof ContratosState)[]).forEach(k => {
            const v = (s as any)[k];
            if (typeof v === 'string') (st as any)[k] = v;
        });
    }
    return st;
}

export const money = (v: number) => (v < 0 ? '−' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const n = (v: number, d = 2) => Number(v).toLocaleString('en-US', { maximumFractionDigits: d });

export type Tono = 'good' | 'bad' | 'warn';

export interface Fila {
    k: string;
    s: string;
    v: string;
    cls?: Tono;
}

export interface Resultado {
    inst: Instrumento;
    hint: string;
    heroLbl: string;
    heroV: string;
    heroU: string;
    rows: Fila[];
    msg: { text: string; cls: Tono } | null;
    // panel de resultado marcado como aviso (stop en el lado incorrecto o 0 contratos)
    warn: boolean;
    // valores numéricos, útiles para probar el módulo
    q: number | null;
    slPts: number | null;
    tpPts: number | null;
    riskPer: number | null;
    slUsd: number | null;
    tpUsd: number | null;
    ratio: number | null;
}

export function calcular(st: ContratosState): Resultado {
    const I = INST.find(i => i.id === st.inst) || INST[0];
    const res: Resultado = {
        inst: I,
        hint: `${I.id}: $${n(I.pv)} por punto · tick ${I.tick} = $${n(I.pv * I.tick)}`,
        heroLbl: 'Resultado', heroV: '—', heroU: '',
        rows: [], msg: null, warn: false,
        q: null, slPts: null, tpPts: null, riskPer: null, slUsd: null, tpUsd: null, ratio: null,
    };
    const show = (text: string, cls: Tono) => { res.msg = { text, cls } };

    const e = parseFloat(st.entry), sl = parseFloat(st.sl), tp = parseFloat(st.tp);
    const sign = st.dir === 'long' ? 1 : -1;

    if (!isFinite(e) || !isFinite(sl)) {
        show('Pon el precio de entrada y el stop loss.', 'warn');
        return res;
    }
    const slPts = (e - sl) * sign;
    if (slPts <= 0) {
        show(st.dir === 'long' ? 'En largo el stop tiene que estar por debajo de la entrada.' : 'En corto el stop tiene que estar por encima de la entrada.', 'bad');
        res.warn = true;
        return res;
    }
    const hasTp = isFinite(tp); const tpPts = hasTp ? (tp - e) * sign : NaN;
    const riskPer = slPts * I.pv;
    res.slPts = slPts; res.riskPer = riskPer;

    let q: number;
    if (st.mode === 'risk') {
        const R = parseFloat(st.risk);
        res.heroLbl = 'Contratos a usar';
        if (!isFinite(R) || R <= 0) { show('Pon cuántos dólares quieres arriesgar.', 'warn'); return res; }
        const raw = Math.floor(R / riskPer + 1e-9);
        q = raw;
        res.heroV = String(q);
        res.heroU = I.id + (q === 1 ? ' contrato' : ' contratos');
        if (raw < 1) { show(`Con este stop, 1 contrato ya arriesga ${money(riskPer)}, más de lo que quieres. Acerca el stop o sube el riesgo.`, 'bad'); res.warn = true }
    } else {
        q = Math.max(0, Math.floor(parseFloat(st.qty) || 0));
        res.heroLbl = 'Pierdes si salta el SL';
        res.heroV = money(-riskPer * q);
        res.heroU = `${q} ${I.id}`;
    }
    res.q = q;
    res.slUsd = -riskPer * q;

    res.rows.push({ k: 'Stop Loss', s: `${n(slPts)} pts · ${n(slPts / I.tick, 1)} ticks`, v: money(-riskPer * q), cls: 'bad' });
    if (hasTp) {
        if (tpPts > 0) {
            res.tpPts = tpPts; res.tpUsd = tpPts * I.pv * q; res.ratio = tpPts / slPts;
            res.rows.push({ k: 'Take Profit', s: `${n(tpPts)} pts · ${n(tpPts / I.tick, 1)} ticks`, v: '+' + money(tpPts * I.pv * q), cls: 'good' });
            res.rows.push({ k: 'Ratio riesgo/beneficio', s: '', v: `1 : ${n(tpPts / slPts, 2)}` });
        } else {
            show(st.dir === 'long' ? 'En largo el take profit tiene que estar por encima de la entrada.' : 'En corto el take profit tiene que estar por debajo de la entrada.', 'bad');
        }
    }
    res.rows.push({ k: 'Riesgo por contrato', s: `${n(slPts)} pts × $${n(I.pv)}`, v: money(riskPer) });
    return res;
}
